import { useBodyMeasurementEntries } from "@/hooks/useBodyMeasurements";
import { useElementWidth } from "@/hooks/useElementWidth";
import {
  aggregateWeekly,
  type BodyMeasurementTypeKey,
  type BodyMeasurementUnit,
  formatEntryDate,
  formatMeasurementValue,
  summarizeTrend,
  type TrendSummary,
  trendAxisRange,
  trendMeasurementType,
  trendWindow,
  trendWindowRange,
} from "@/lib/body-measurements";
import { isoWeekOf } from "@/lib/days";
import type { BodyMeasurementTrendConfig } from "@/lib/schema";
import type { WidgetWeekViewProps } from "@/widgets/types";
import {
  type AxisScale,
  axisGutter,
  axisScale,
  axisTickLabel,
  type ChartLayout,
  chartLayout,
  gridlinePositions,
  isLabelled,
  labelStride,
  requiredDomain,
  seriesGeometry,
  VALUE_LABEL_WIDTH,
  valueLabelPositions,
  WEEK_LABEL_WIDTH,
  xPosition,
} from "./chart";

type Props = WidgetWeekViewProps<BodyMeasurementTrendConfig>;

/**
 * Chart identity is keyed to the measurement type in catalog order and is
 * deliberately independent of the user-switchable `--accent` token: one of
 * its four options (`moss`) is identical to `--good`, which used to render
 * two series in the same colour. The pair passes all six palette checks
 * (lightness, chroma, CVD separation, normal-vision separation, contrast)
 * against both the light and dark card surfaces.
 */
const SERIES_COLORS: Record<BodyMeasurementTypeKey, string> = {
  weight: "var(--chart-series-weight)",
  abdomen: "var(--chart-series-abdomen)",
};

/**
 * Width of the horizontal caps that mark a week's min and max. The capsule
 * bar this replaced was 6 wide with `rx 3`, so both bounds sat at the tip
 * of a curve tapering to zero width — the two values the mark exists to
 * report were the least-inked part of it, and the equally-wide average dot
 * covered what was left. Caps put min and max at pointable positions.
 */
const WHISKER_CAP_WIDTH = 10;

export function WeekView({ config, week }: Props) {
  // Anchored to the viewed week; the settings preview (no week) ends the
  // window at the current ISO week.
  const anchor = week
    ? { year: week.year, weekNumber: week.weekNumber }
    : isoWeekOf(new Date());
  const weeks = trendWindow(anchor.year, anchor.weekNumber);
  const range = trendWindowRange(anchor.year, anchor.weekNumber);
  const { entries, loading } = useBodyMeasurementEntries(
    range.startDate,
    range.endDate,
  );

  const type = trendMeasurementType(config);
  const aggregates = aggregateWeekly(entries, type.key, weeks);
  const summary = summarizeTrend(entries, type.key, weeks);
  const required = requiredDomain(
    aggregates,
    trendAxisRange(config, type.unit),
  );

  // The SVG is drawn at 1:1, so it needs its own rendered width before it
  // can place anything; `measuredWidth` is 0 until the observer first fires.
  const [measuredWidth, measureChart] = useElementWidth();

  // Scale first: the axis gutter is sized from the labels it will hold.
  const scale = required === null ? null : axisScale(required);
  const layout = chartLayout(measuredWidth, axisGutter(scale, type.unit));
  const geometry =
    scale === null ? null : seriesGeometry(aggregates, scale, layout);
  const gridlines = scale === null ? [] : gridlinePositions(scale, layout);
  const labels =
    geometry === null
      ? new Map<number, number>()
      : valueLabelPositions(geometry.points, layout);
  const color = SERIES_COLORS[type.key];
  const weekStride = labelStride(weeks.length, layout, WEEK_LABEL_WIDTH);
  const valueStride = labelStride(weeks.length, layout, VALUE_LABEL_WIDTH);

  const hasAnyData = aggregates.some((a) => a !== null);

  return (
    <section
      className="widget-card body-measurement-trend"
      data-testid={`widget-week-${config.id}`}
    >
      <header className="widget-card-head">
        <h3 className="widget-card-title">{config.label}</h3>
      </header>
      <div className="widget-card-body">
        {loading ? (
          <p className="hint">Laster…</p>
        ) : !hasAnyData || geometry === null || scale === null ? (
          <p className="hint" data-testid={`trend-empty-${config.id}`}>
            Ingen målinger i de siste {weeks.length} ukene enda.
          </p>
        ) : (
          <div className="trend-chart-wrap" ref={measureChart}>
            {measuredWidth > 0 ? (
              <svg
                className="trend-chart"
                width={layout.width}
                height={layout.height}
                viewBox={`0 0 ${layout.width} ${layout.height}`}
                role="img"
                aria-label={`${config.label} (${type.unit})`}
                data-testid={`trend-chart-${config.id}`}
              >
                {gridlines.slice(1).map((y) => (
                  <line
                    key={y}
                    className="trend-gridline"
                    x1={layout.plotLeft}
                    y1={y}
                    x2={layout.plotRight}
                    y2={y}
                  />
                ))}
                <line
                  className="trend-frame"
                  x1={layout.plotLeft}
                  y1={layout.plotBottom}
                  x2={layout.plotRight}
                  y2={layout.plotBottom}
                />
                <AxisTicks
                  unit={type.unit}
                  scale={scale}
                  gridlines={gridlines}
                  layout={layout}
                />
                {weeks.map((w, i) =>
                  isLabelled(i, weeks.length, weekStride) ? (
                    <text
                      key={`${w.year}-${w.weekNumber}`}
                      className="trend-x-label"
                      x={xPosition(i, weeks.length, layout)}
                      y={layout.plotBottom + 16}
                      textAnchor="middle"
                    >
                      Uke {w.weekNumber}
                    </text>
                  ) : null,
                )}
                <g data-testid={`trend-series-${type.key}`}>
                  {geometry.linePaths.map((d) => (
                    <path
                      key={d}
                      d={d}
                      fill="none"
                      stroke={color}
                      strokeWidth={2}
                    />
                  ))}
                  {geometry.points.map((p) => {
                    const w = weeks[p.index];
                    return (
                      <circle
                        key={p.index}
                        cx={p.x}
                        cy={p.y}
                        r={3}
                        fill={color}
                        data-testid={`trend-point-${type.key}-${w.year}-${w.weekNumber}`}
                      />
                    );
                  })}
                  {/* After the dot on purpose: a week whose spread is smaller
                      than the dot would otherwise have both caps painted over,
                      and padding the whisker to a minimum height would
                      misreport the data. */}
                  {geometry.points
                    .filter((p) => p.hasSpread)
                    .map((p) => {
                      const w = weeks[p.index];
                      const half = WHISKER_CAP_WIDTH / 2;
                      return (
                        <g
                          key={p.index}
                          className="trend-spread"
                          stroke={color}
                          data-testid={`trend-spread-${type.key}-${w.year}-${w.weekNumber}`}
                        >
                          <line x1={p.x} y1={p.top} x2={p.x} y2={p.bottom} />
                          <line
                            x1={p.x - half}
                            y1={p.top}
                            x2={p.x + half}
                            y2={p.top}
                          />
                          <line
                            x1={p.x - half}
                            y1={p.bottom}
                            x2={p.x + half}
                            y2={p.bottom}
                          />
                        </g>
                      );
                    })}
                  {geometry.points.map((p) => {
                    const w = weeks[p.index];
                    const y = labels.get(p.index);
                    if (y === undefined) return null;
                    if (!isLabelled(p.index, weeks.length, valueStride))
                      return null;
                    return (
                      <text
                        key={p.index}
                        className="trend-value-label"
                        x={p.x}
                        y={y}
                        textAnchor="middle"
                        data-testid={`trend-value-${type.key}-${w.year}-${w.weekNumber}`}
                      >
                        {formatMeasurementValue(p.value)}
                      </text>
                    );
                  })}
                </g>
              </svg>
            ) : null}
          </div>
        )}
        <SummaryRow
          configId={config.id}
          typeKey={type.key}
          label={type.label}
          unit={type.unit}
          summary={summary}
        />
      </div>
    </section>
  );
}

function AxisTicks({
  unit,
  scale,
  gridlines,
  layout,
}: {
  unit: BodyMeasurementUnit;
  scale: AxisScale;
  gridlines: number[];
  layout: ChartLayout;
}) {
  return (
    <g className="trend-axis" data-testid={`trend-axis-${unit}`}>
      {scale.ticks.map((tick, i) => (
        <text
          key={tick}
          className="trend-axis-label"
          x={layout.plotLeft - 6}
          y={gridlines[i] + 3.5}
          textAnchor="end"
          data-testid={`trend-axis-tick-${unit}-${i}`}
        >
          {axisTickLabel(tick, scale.decimals, unit)}
        </text>
      ))}
    </g>
  );
}

function SummaryRow({
  configId,
  typeKey,
  label,
  unit,
  summary,
}: {
  configId: string;
  typeKey: BodyMeasurementTypeKey;
  label: string;
  unit: BodyMeasurementUnit;
  summary: TrendSummary;
}) {
  const latest = summary.latest
    ? `${formatMeasurementValue(summary.latest.value)} ${unit} · ${formatEntryDate(summary.latest.date)}`
    : "—";
  const change =
    summary.change === null
      ? "—"
      : `${formatMeasurementValue(summary.change)} ${unit}`;
  const changePerWeek =
    summary.changePerWeek === null
      ? "—"
      : `${formatMeasurementValue(summary.changePerWeek, 2)} ${unit}`;
  const lowest =
    summary.lowest === null
      ? "—"
      : `${formatMeasurementValue(summary.lowest)} ${unit}`;
  const highest =
    summary.highest === null
      ? "—"
      : `${formatMeasurementValue(summary.highest)} ${unit}`;
  return (
    <div
      className="trend-summary"
      data-testid={`trend-summary-${configId}-${typeKey}`}
    >
      <span className="trend-summary-label">{label}</span>
      <span data-testid={`trend-latest-${configId}-${typeKey}`}>
        Siste: {latest}
      </span>
      <span data-testid={`trend-change-${configId}-${typeKey}`}>
        Endring: {change}
      </span>
      <span data-testid={`trend-change-per-week-${configId}-${typeKey}`}>
        Snitt/uke: {changePerWeek}
      </span>
      <span data-testid={`trend-lowest-${configId}-${typeKey}`}>
        Laveste: {lowest}
      </span>
      <span data-testid={`trend-highest-${configId}-${typeKey}`}>
        Høyeste: {highest}
      </span>
    </div>
  );
}
